import L from "leaflet";
import { BaseMapRender, PopupConfig } from "../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../implementations/map/TopActions";
import { storageTilesSasReadService } from "../../../../comun/services/storage/StorageTilesSasReadService";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { fx_createEscenaGrpcService } from "../../../../modulos/satelite/services/EscenaGrpcService";
import { EscenaResponse } from "../../../../modulos/satelite/response/EscenaResponse";
import { diasDescanso, diasOcupacion } from "../../../../utils/ganaderiaUtils";
import { _miles } from "../../../../utils/numberUtils";
import { SateliteBottomSheetRender } from "./SateliteBottomSheetRender";

/** Tile transparente (1x1 gif) para tiles faltantes: evita el icono de "imagen rota". */
const TRANSPARENT_TILE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/** Mapa del módulo Satélite. El bottom-sheet queda siempre abierto (capa,
 *  fechas y leyenda), independiente de si hay un lote seleccionado — se monta
 *  apenas el mapa termina de inicializar, no vía `onResponseSelected`. Sin
 *  Ortomosaico en ningún lado (ni TopActions ni el select): solo índices
 *  satelitales, elegidos en el bottom-sheet y armados acá, notificados por el
 *  propio bottom-sheet. */
export class SateliteMapRender extends BaseMapRender<ClienteFincaLoteResponse> {
  constructor() {
    super();
    this.setOnMapInitializeEnd((map) => {
      this._map = map;
      this._mountBaseSheet();
    });
  }

  /** Precarga las escenas satelitales de la finca (data de finca, no por-lote:
   *  se guarda como efecto secundario para el bottom-sheet). El lote ya trae
   *  lo necesario para el tooltip default, así que no hay `TResponse[]` real. */
  protected async doInitData(): Promise<ClienteFincaLoteResponse[]> {
    this._escenas = await fx_createEscenaGrpcService(
      this._finca.id,
    ).getListAsync();
    return [];
  }

  /** El "response" del lote es el propio lote. */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: ClienteFincaLoteResponse[],
  ): ClienteFincaLoteResponse | null {
    return lote;
  }

  /** Libera el bottom-sheet base y la capa activa si quedaron montados. */
  protected doDispose(): void {
    this._baseSheet?.dispose();
    this._removeCapaLayer();
  }

  /** Tooltip default + campos propios del tipo de finca (Cultivo en
   *  agricultura; Animales/Días descanso/Días ocupación en ganadería — mismos
   *  campos que agregan AgriculturaMapRender/GanaderiaMapRender por separado,
   *  acá combinados porque Satélite aplica a ambos tipos). Sin ortomosaico ni
   *  conteo en TopActions — Ortomosaico no vive en ningún lado de este mapa. */
  protected getConfigTopActions(): ConfigTopActions<ClienteFincaLoteResponse> {
    const toolTipItems = this.getDefaultTooltipItems();

    if (this._finca.isAgricultura) {
      toolTipItems.push({
        get(_response, lote) {
          return lote.cultivo?.nombre ?? "-";
        },
        key: "cultivo",
        label: "Cultivo",
      });
    }

    if (this._finca.isGanaderia) {
      toolTipItems.push(
        {
          get(_response, lote) {
            return _miles(lote.ganaderiaCantidadAnimales);
          },
          key: "animales",
          label: "Animales",
        },
        {
          get(_response, lote) {
            const dias = diasDescanso(lote);
            return dias != null ? `${dias} días` : "—";
          },
          key: "diasDescanso",
          label: "Días descanso",
        },
        {
          get(_response, lote) {
            const dias = diasOcupacion(lote);
            return dias != null ? `${dias} días` : "—";
          },
          key: "diasOcupacion",
          label: "Días ocupación",
        },
      );
    }

    return {
      toolTipItems,
      withOrtomosaico: false,
      withConteo: false,
    };
  }

  /** Sin popup por ahora (la sección de lote seleccionado es un paso posterior). */
  protected getPopupConfig(): PopupConfig<ClienteFincaLoteResponse> | null {
    return null;
  }

  /** Al seleccionar un lote: fuerza el bottom-sheet al 50% (deja espacio para
   *  el chart) y monta su sección (título + datos + chart). */
  protected onResponseSelected(
    lote: ClienteFincaLoteResponse,
    _response: ClienteFincaLoteResponse | null,
    bottomSheet: HTMLDivElement,
  ): void {
    bottomSheet.classList.add("sbs-lote-abierto");
    this._baseSheet?.setLoteSeleccionado(lote, this._finca);
    requestAnimationFrame(() => this.invalidateMapSize());
  }

  /** Al deseleccionar: desmonta la sección y el bottom-sheet vuelve a su alto natural. */
  protected onLoteRemove(bottomSheet: HTMLDivElement): void {
    bottomSheet.classList.remove("sbs-lote-abierto");
    this._baseSheet?.setLoteSeleccionado(null, null);
    requestAnimationFrame(() => this.invalidateMapSize());
  }

  /** Mapa Leaflet (disponible tras `setOnMapInitializeEnd`). */
  private _map: L.Map | null = null;
  /** Escenas satelitales de la finca (disponibles tras `doInitData`). */
  private _escenas: EscenaResponse[] = [];
  /** Bottom-sheet base (capa/fechas/leyenda), montado una sola vez. */
  private _baseSheet: SateliteBottomSheetRender | null = null;
  /** Capa de tiles del índice activo, o null si ninguno tiene tile disponible. */
  private _capaLayer: L.TileLayer | null = null;
  /** Secuencia de pedidos de capa: descarta respuestas de SAS obsoletas si el
   *  usuario cambió de capa/escena mientras una petición seguía en vuelo. */
  private _capaRequestSeq = 0;

  /** Monta el bottom-sheet base y lo deja abierto de entrada (no depende de
   *  seleccionar un lote): busca el contenedor directo, ya que `BaseMapRender`
   *  solo lo entrega vía los hooks de selección de lote. */
  private _mountBaseSheet(): void {
    const bottomSheet = document.querySelector<HTMLDivElement>(
      ".map-bottom-sheet",
    );
    if (!bottomSheet) return;

    this._baseSheet = new SateliteBottomSheetRender(
      this._escenas,
      (capaId, escena) => this._setCapa(capaId, escena),
    );
    bottomSheet.innerHTML = this._baseSheet.render();
    this._baseSheet.connect();
    bottomSheet.classList.add("open");
    // El sheet le quita altura al mapa (flex): recalcular el tamaño de Leaflet.
    requestAnimationFrame(() => this.invalidateMapSize());
  }

  /** Arma (o quita) la capa de tiles del índice elegido en el bottom-sheet:
   *  busca su tile en la escena activa y pide SAS de lectura (mismo
   *  contenedor privado usado por ortomosaico/conteo en otros mapas).
   *  Descarta la respuesta si el usuario ya cambió de capa/escena mientras el
   *  SAS estaba en vuelo. */
  private async _setCapa(
    capaId: string,
    escena: EscenaResponse | null,
  ): Promise<void> {
    const seq = ++this._capaRequestSeq;
    this._removeCapaLayer();
    if (!this._map || !capaId) return;

    const template =
      escena?.indices.find((item) => item.indiceId === capaId)?.imageUri ??
      null;
    if (!template) return;

    try {
      const sas = await storageTilesSasReadService.getAsync();
      if (seq !== this._capaRequestSeq || !this._map) return;

      this._capaLayer = L.tileLayer(`${template}?${sas.sasToken}`, {
        maxZoom: 20,
        minNativeZoom: 16,
        maxNativeZoom: 17,
        errorTileUrl: TRANSPARENT_TILE,
      }).addTo(this._map);
    } catch {
      // Sin SAS: no se muestra la capa.
    }
  }

  /** Remueve la capa de tiles activa (si hay). */
  private _removeCapaLayer(): void {
    this._capaLayer?.remove();
    this._capaLayer = null;
  }
}
